import { useEffect, useState } from 'react';
import InteractiveTour from './InteractiveTour';
import api from '../api';

/**
 * ContextualTour
 *
 * Auto-launches a specific mini-tour (identified by `tourId`) the first time
 * the current user reaches a specific module. Persists progress via the
 * shared `/api/tours/progress` endpoints so it never repeats unless
 * explicitly reset.
 *
 * Usage:
 *   <ContextualTour tourId="sim-first-time" ready={simulationLoaded} />
 *
 * Props:
 *  - tourId (string): id from TourRegistry mini-tours
 *  - ready (bool): whether the parent DOM is ready for spotlights (default true)
 *  - delay (ms): how long to wait after `ready` becomes true (default 1200)
 */
export default function ContextualTour({ tourId, ready = true, delay = 1200 }) {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!ready || checked) return;
    (async () => {
      try {
        const resp = await api.get('/tours/progress');
        const p = (resp.data?.progress || {})[tourId];
        if (!p || (!p.completed && !p.dont_show_again && !p.skipped)) {
          setTimeout(() => setOpen(true), delay);
        }
      } catch {
        // Silent fail — the mini tour is optional
      } finally {
        setChecked(true);
      }
    })();
  }, [ready, checked, tourId, delay]);

  if (!open) return null;
  return <InteractiveTour isOpen={open} onClose={() => setOpen(false)} tourId={tourId} />;
}
